class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.hoverTimeout = null;
    this.imagesLoaded = false;
    this.emblaApi = null;
    this._intersectionObserver = null;
    this._modeApplied = false;
    this._boundHandleHover = this.handleHover.bind(this);
    this._boundClearHover = this.clearHover.bind(this);
    this._boundHandleResize = this.handleResize.bind(this);
    this._boundPrevClick = null;
    this._boundNextClick = null;

    this.productForm = this.querySelector("product-form");
    if (this.productForm) {
      this.submitButton = this.productForm.querySelector("[type='submit']");
      this.addCartText = this.submitButton
        ?.querySelector("[data-add-to-cart-text]")
        ?.getAttribute("data-add-to-cart-text");
    }

    this.isTouchDevice = ProductCard.isTouchLikeDevice();
  }

  /** Mobile/no-hover UI: load in-view and show bullets instead of arrows */
  static isTouchLikeDevice() {
    return (
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.matchMedia("(max-width: 900px)").matches ||
      (navigator.maxTouchPoints > 0 &&
        window.matchMedia("(hover: none)").matches)
    );
  }

  connectedCallback() {
    if (this.productForm) {
      const variants = this.querySelectorAll("[data-variant-id]");
      variants.forEach((variant) => {
        variant.addEventListener("click", () =>
          this.handleVariantClick(variant, variants)
        );
      });
    }

    this.applyInputMode(true);
    window.addEventListener("resize", this._boundHandleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._boundHandleResize);
    this.removeEventListener("mouseenter", this._boundHandleHover);
    this.removeEventListener("mouseleave", this._boundClearHover);
    this.removeEmblaArrowHandlers();
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
    this.emblaApi?.destroy?.();
    this.emblaApi = null;
    clearTimeout(this.hoverTimeout);
  }

  handleResize() {
    this.applyInputMode();
  }

  applyInputMode(force = false) {
    const nextIsTouchDevice = ProductCard.isTouchLikeDevice();
    if (!force && this._modeApplied && nextIsTouchDevice === this.isTouchDevice) return;

    this.isTouchDevice = nextIsTouchDevice;
    this._modeApplied = true;

    if (this.isTouchDevice) {
      this.dataset.touchUi = "true";
      this.removeEventListener("mouseenter", this._boundHandleHover);
      this.removeEventListener("mouseleave", this._boundClearHover);
      this.removeEmblaArrowHandlers();
      this.clearHover();
      this.setupTouchInView();
      if (this.imagesLoaded && this.emblaApi) this.initDots(this.emblaApi);
      return;
    }

    delete this.dataset.touchUi;
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
    this.addEventListener("mouseenter", this._boundHandleHover);
    this.addEventListener("mouseleave", this._boundClearHover);
    if (this.imagesLoaded && this.emblaApi) this.setupEmblaArrowHandlers(this.emblaApi);

    const dotsRoot = this.querySelector(".product-card__dots");
    if (dotsRoot) dotsRoot.replaceChildren();
  }

  setupTouchInView() {
    if (this.imagesLoaded) return;

    const urls = (this.dataset.imgUrls || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      this.imagesLoaded = true;
      this.dataset.imagesLoaded = "true";
      return;
    }

    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.imagesLoaded) return;

          this._intersectionObserver?.disconnect();
          this._intersectionObserver = null;

          this.dataset.imagesLoading = "true";

          this.loadImages()
            .then(() => this.initEmblaSlider())
            .catch(console.error)
            .finally(() => {
              delete this.dataset.imagesLoading;
            });
        });
      },
      {
        root: null,
        rootMargin: "80px 0px 120px 0px",
        threshold: 0.08,
      }
    );

    this._intersectionObserver.observe(this);
  }

  handleVariantClick(variant, variants) {
    if (!this.submitButton || !this.productForm?.variantIdInput) return;

    this.submitButton.disabled = false;
    const span = this.submitButton.querySelector("span");
    if (span && this.addCartText) span.textContent = this.addCartText;

    const variantId = variant.dataset.variantId;
    this.productForm.variantIdInput.value = variantId;

    variants.forEach((allVariants) => {
      allVariants.classList.remove("bg-black", "text-white");
    });

    variant.classList.add("bg-black", "text-white");
  }

  handleHover() {
    if (this.imagesLoaded) return;

    const urls = (this.dataset.imgUrls || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      this.imagesLoaded = true;
      this.dataset.imagesLoaded = "true";
      return;
    }

    this.dataset.imagesLoading = "true";

    this.hoverTimeout = setTimeout(() => {
      this.loadImages()
        .then(() => this.initEmblaSlider())
        .catch(console.error)
        .finally(() => {
          delete this.dataset.imagesLoading;
        });
    }, 200);
  }

  clearHover() {
    clearTimeout(this.hoverTimeout);
    if (!this.imagesLoaded) {
      delete this.dataset.imagesLoading;
    }
  }

  async loadImages() {
    const imageContainer = this.querySelector(".embla__container");
    if (!imageContainer) return;

    const urls = (this.dataset.imgUrls || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      this.imagesLoaded = true;
      this.dataset.imagesLoaded = "true";
      return;
    }

    const loadPromises = [];

    urls.forEach((url) => {
      const slide = document.createElement("div");
      slide.className = "image product-card__image-slide cover aspect-13/21 sm:aspect-square";

      const img = document.createElement("img");
      img.src = url;
      img.alt = "";
      img.loading = "eager";
      img.sizes = "(min-width: 768px) 25vw, 50vw";

      slide.appendChild(img);
      imageContainer.appendChild(slide);

      if (!img.complete) {
        loadPromises.push(
          new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          })
        );
      }
    });

    await Promise.all(loadPromises);

    this.imagesLoaded = true;
    this.dataset.imagesLoaded = "true";
  }

  initEmblaSlider() {
    if (this.emblaApi) return;

    const sliderNode = this.querySelector(".embla");
    if (!sliderNode || typeof EmblaCarousel === "undefined") return;

    const emblaApi = EmblaCarousel(sliderNode, {
      loop: true,
      align: "start",
    });

    this.emblaApi = emblaApi;

    if (this.isTouchDevice) {
      this.initDots(emblaApi);
    } else {
      this.setupEmblaArrowHandlers(emblaApi);
    }
  }

  setupEmblaArrowHandlers(emblaApi) {
    const prevButton = this.querySelector(".product-card__control--prev");
    const nextButton = this.querySelector(".product-card__control--next");

    this.removeEmblaArrowHandlers();
    this._boundPrevClick = () => emblaApi.scrollPrev();
    this._boundNextClick = () => emblaApi.scrollNext();

    prevButton?.addEventListener("click", this._boundPrevClick);
    nextButton?.addEventListener("click", this._boundNextClick);
  }

  removeEmblaArrowHandlers() {
    const prevButton = this.querySelector(".product-card__control--prev");
    const nextButton = this.querySelector(".product-card__control--next");
    if (this._boundPrevClick) prevButton?.removeEventListener("click", this._boundPrevClick);
    if (this._boundNextClick) nextButton?.removeEventListener("click", this._boundNextClick);
    this._boundPrevClick = null;
    this._boundNextClick = null;
  }

  initDots(emblaApi) {
    const dotsRoot = this.querySelector(".product-card__dots");
    if (!dotsRoot) return;

    const snaps = emblaApi.scrollSnapList();
    dotsRoot.replaceChildren();

    snaps.forEach((_, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "product-card__dot";
      btn.role = "tab";
      btn.setAttribute("aria-label", `Image ${i + 1} of ${snaps.length}`);
      btn.addEventListener("click", () => emblaApi.scrollTo(i));
      dotsRoot.appendChild(btn);
    });

    const syncDots = () => {
      const selected = emblaApi.selectedScrollSnap();
      dotsRoot.querySelectorAll(".product-card__dot").forEach((dot, i) => {
        const on = i === selected;
        dot.classList.toggle("is-selected", on);
        dot.setAttribute("aria-selected", on ? "true" : "false");
      });
    };

    emblaApi.on("select", syncDots);
    emblaApi.on("reInit", syncDots);
    syncDots();
  }
}

customElements.define("product-card", ProductCard);
